import { EmptyTable } from "@/components/Datatable/components/EmptyTable";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TableLoad } from "@/components/ui/table-load";
import { cn } from "@/lib/utils";
import { PendingBatch } from "@/services/price-remark/types";
import { formatDateToDMY } from "@/utils/format-date-ddmmyyyy";
import { formatNumber, formatPercentage } from "@/utils/formatToMoney";

import { AGE_ALERT_DAYS } from "../constants";
import { pluralizeDays } from "../format";
import { BatchStatusBadge } from "./situation-badge";

const COLUMN_COUNT = 11;

interface BatchesTableProps {
  batches: PendingBatch[];
  isLoading: boolean;
  selected: string | null;
  onSelect: (batch: string) => void;
}

export function BatchesTable({
  batches,
  isLoading,
  selected,
  onSelect,
}: BatchesTableProps) {
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader className="bg-[#F9FBFC]">
          <TableRow>
            <TableHead className="text-center">Lote</TableHead>
            <TableHead className="text-center">Idade</TableHead>
            <TableHead className="text-center">Itens</TableHead>
            <TableHead className="text-center">c/ estoque</TableHead>
            <TableHead className="text-center">s/ estoque</TableHead>
            <TableHead className="text-center">Não inic.</TableHead>
            <TableHead className="text-center">Parcial</TableHead>
            <TableHead className="text-center">Justif.</TableHead>
            <TableHead className="text-center">Falha</TableHead>
            <TableHead className="text-center">
              <span
                className="cursor-help border-b border-dotted border-gray-400"
                title="Itens com ao menos uma etiqueta ÷ itens com estoque. Mede se o operador passou pelo item, não se terminou — por isso um lote pode ter 100 % e ainda ter parciais."
              >
                Cobertura
              </span>
            </TableHead>
            <TableHead className="text-center">Situação</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {isLoading ? (
            <TableLoad colSpan={COLUMN_COUNT} />
          ) : batches.length === 0 ? (
            <TableRow>
              <TableCell colSpan={COLUMN_COUNT} className="h-24 text-center">
                <EmptyTable text="Nenhum lote a remarcar nesta janela." />
              </TableCell>
            </TableRow>
          ) : (
            batches.map((batch) => (
              <TableRow
                key={batch.batch}
                tabIndex={0}
                className={cn(
                  "cursor-pointer",
                  selected === batch.batch &&
                    "bg-amber-50 shadow-[inset_3px_0_0_#F5CB2B] hover:bg-amber-50",
                )}
                onClick={() => onSelect(batch.batch)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    onSelect(batch.batch);
                  }
                }}
              >
                <TableCell className="whitespace-nowrap text-center font-semibold">
                  {formatDateToDMY(batch.batch)}
                </TableCell>
                <TableCell
                  className={cn(
                    "whitespace-nowrap text-center text-gray-500",
                    batch.ageDays >= AGE_ALERT_DAYS &&
                      "font-semibold text-red-600",
                  )}
                >
                  {pluralizeDays(batch.ageDays)}
                </TableCell>
                <TableCell className="text-center tabular-nums">
                  {formatNumber(batch.items)}
                </TableCell>
                <TableCell className="text-center tabular-nums">
                  {formatNumber(batch.inStock)}
                </TableCell>
                <TableCell className="text-center tabular-nums text-gray-400">
                  {formatNumber(batch.outOfStockTotal)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-center tabular-nums",
                    batch.notStarted > 0
                      ? "font-semibold text-red-600"
                      : "text-gray-400",
                  )}
                >
                  {formatNumber(batch.notStarted)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-center tabular-nums",
                    batch.partial > 0 ? "text-amber-600" : "text-gray-400",
                  )}
                >
                  {formatNumber(batch.partial)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-center tabular-nums",
                    batch.justified === 0 && "text-gray-400",
                  )}
                >
                  {formatNumber(batch.justified)}
                </TableCell>
                <TableCell
                  className={cn(
                    "text-center tabular-nums",
                    batch.printFailure > 0
                      ? "font-semibold text-red-600"
                      : "text-gray-400",
                  )}
                >
                  {formatNumber(batch.printFailure)}
                </TableCell>
                <TableCell className="text-center font-semibold tabular-nums">
                  {batch.coverage == null ? "—" : formatPercentage(batch.coverage, 1)}
                </TableCell>
                <TableCell className="whitespace-nowrap text-center">
                  <BatchStatusBadge status={batch.status} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </div>
  );
}

export default BatchesTable;
